// Native shell integration for the Capacitor (iOS/Android) builds.
// On the web this is a no-op, so the same bundle runs everywhere.
//
// IMPORTANT: each concern is isolated in its own try/catch with its own import,
// so a failure in one (e.g. status bar) can never prevent another (e.g. hiding
// the splash screen) from running. Getting the splash hidden is the top priority.

import { Capacitor } from '@capacitor/core';

const ROOT_ROUTES = ['/', '/swipe', '/host-dashboard'];

export async function initNative() {
  if (!Capacitor.isNativePlatform()) return;
  const platform = Capacitor.getPlatform();

  // --- 1. Hide the splash screen FIRST, on its own, so nothing can block it ---
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (_) {}

  // --- 2. Status bar ---
  // The top of every screen is the ink header band in BOTH themes, so the bar
  // always wants light glyphs. ThemeContext.applyTheme() sets this too (and
  // re-sets it whenever the theme changes); this call just covers the native
  // cold-start path in case the theme prime ran before the plugin was ready.
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    if (platform === 'android') {
      await StatusBar.setBackgroundColor({ color: '#0B1B33' });
    }
  } catch (_) {}

  // --- 3. Android hardware back button ---
  try {
    const { App } = await import('@capacitor/app');
    App.addListener('backButton', ({ canGoBack }) => {
      if (ROOT_ROUTES.includes(window.location.pathname)) {
        App.minimizeApp();
      } else if (canGoBack) {
        window.history.back();
      } else {
        App.minimizeApp();
      }
    });
  } catch (_) {}

  // --- 4. Stripe Checkout return (deep-link back into the app) ---
  // Registers the appUrlOpen / resume listeners so a completed checkout reopens
  // the app on the right screen instead of stranding the user on the website.
  try {
    const { installCheckoutReturn } = await import('./checkout-return');
    await installCheckoutReturn();
  } catch (_) {}

  // --- 5. Apple In-App Purchase recovery (iOS) ---
  // Re-sends any purchase StoreKit still holds as unfinished (app was killed
  // before the server confirmed it) and forwards late ones (Ask to Buy).
  if (platform === 'ios') {
    try {
      const { installIapRecovery } = await import('./iap');
      installIapRecovery(); // not awaited — never delay startup on the network
    } catch (_) {}
  }
}
